"use client"
import React from "react"
import { ExpandAll, ExpandLess, ExpandMore, IconComponent } from "../../icons"
import { classNames } from "../../utils"
import { CenterAligned } from "../CenterAligned"
import { textLabelVariants } from "../Text/TextLabel"
import { TableCell, TableCellProps } from "./TableCell"
import { SortOrder } from "./types"

export type TableColumnSort<T> = {
  column: T
  order?: SortOrder
}

type TableHeaderCellProps<T> = TableCellProps & {
  sort?: TableColumnSort<T> & { onSort: (newSort: TableColumnSort<T>) => void }
}

const getIconComponent = (sortOrder: SortOrder | undefined): IconComponent => {
  switch (sortOrder) {
    case "asc":
      return ExpandLess
    case "desc":
      return ExpandMore
    case undefined:
      return ExpandAll
  }
}

const SortIcon = ({ sortOrder }: { sortOrder: SortOrder | undefined }) => {
  const IconComponent = getIconComponent(sortOrder)
  return <IconComponent size={12} />
}

// TODO: Add forward ref when it's needed, there are some issues with passing in generics to forwardRef
// which will need some manual casting/wrapper
export const TableHeaderCell = <T,>({
  children,
  sort,
  className,
  ...cellProps
}: TableHeaderCellProps<T>) => {
  return (
    <TableCell
      className={classNames(
        sort && "cursor-pointer",
        sort?.order ? "font-medium text-text-primary" : "text-text-secondary",
        textLabelVariants({ size: "xs" }),
        className,
      )}
      role="columnheader"
      onClick={() =>
        sort?.onSort({
          column: sort.column,
          order: sort.order === "desc" ? "asc" : "desc",
        })
      }
      {...cellProps}
    >
      {children}

      {sort && (
        // Better aligns the sort icon with the cell content
        <CenterAligned className="relative left-[3px]">
          <SortIcon sortOrder={sort.order} />
        </CenterAligned>
      )}
    </TableCell>
  )
}
